<template>
    <div class="flex flex-col gap-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-full">
            <FormContainer class="w-full">
                <Label :label="$t('profileSection.name')"/>
                <Input :placeholder="$t('profileSection.name')" v-model="profile.name"/>
            </FormContainer>
            <FormContainer class="w-full">
                <Label :label="$t('profileSection.email')"/>
                <Input :placeholder="$t('profileSection.email')" v-model="profile.email" disabled readonly class="!text-gray-400"/>
            </FormContainer>
            <FormContainer class="w-full">
                <Label :label="$t('profileSection.timezone')"/>
                <SelectOption class="w-full h-[42px] dark:bg-slate-800 dark:border-slate-700 dark:text-white" v-model="profile.timezone" :options="timezones" />
            </FormContainer>
            <FormContainer class="w-full">
                <Label :label="$t('menu.language') || 'Language'"/>
                <select 
                    v-model="currentLanguage" 
                    @change="changeLanguage"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 w-full h-[42px] dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                >
                    <option value="en">EN</option>
                    <option value="pt-BR">PT-BR</option>
                    <option value="es">ES</option>
                    <option value="fr">FR</option>
                </select>
            </FormContainer>
            
            <div class="w-full col-span-1 md:col-span-2 mt-2">
                <Button class="!bg-gradient-to-r !from-blue-500 !to-purple-500 hover:!from-blue-600 hover:!to-purple-600 !border-none !text-white !cursor-pointer transition-all rounded-lg px-6 py-2" @click="saveProfile()" :disabled="saving">
                    {{ $t('profileSection.saveChanges') }}
                </Button>
                <Loading v-if="saving"/>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import userStore from '@/store/user.store'
import toastStore from '@/store/toast.store'
import SelectOption from '@/components/form/SelectOption.vue'
import { useI18n } from 'vue-i18n';
import { setLanguage } from '@/i18n';

const profile = ref({
    name: '',
    email: '',
    timezone: ''
})
const timezones = ref(typeof Intl !== 'undefined' && Intl.supportedValuesOf ? Intl.supportedValuesOf('timeZone') : ['UTC'])
const saving = ref(false)

const { t, locale } = useI18n();
const currentLanguage = ref(locale.value);

const changeLanguage = () => {
  setLanguage(currentLanguage.value);
};

onMounted(async () => {
    try {
        const response = await fetch('/api/user/info')
        const data = await response.json()
        const localTz = typeof Intl !== 'undefined' && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC'
        profile.value = {
            name: data.name || '',
            email: data.email || '',
            timezone: data.timezone || localTz
        }
    } catch (error) {
        toastStore.dispatch('addMessage', {
            message: t('profileSection.profileLoadFailed') || 'Failed to load profile information',
            icon: 'error'
        })
    }
})

const saveProfile = async () => {
    saving.value = true
    try {
        const response = await fetch('/api/user/profile', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(profile.value)
        })
        
        if (response.ok) {
            toastStore.dispatch('addMessage', {
                message: t('profileSection.profileUpdated') || 'Profile updated successfully',
                icon: 'success'
            })
            // Update the user info in the store
            userStore.dispatch('updateBasicInfo')
        } else {
            throw new Error('Failed to update profile')
        }
    } catch (error) {
        toastStore.dispatch('addMessage', {
            message: t('profileSection.profileUpdateFailed') || 'Failed to update profile',
            icon: 'error'
        })
    } finally {
        saving.value = false
    }
}
</script> 